import { useState, useEffect } from 'react';

interface WishlistVehicle {
  id: number;
  name: string;
  price: number;
  mileage: string;
  location: string;
  badge?: string;
  images: string[];
  addedDate: string;
}

export function useWishlist() {
  const [wishlistIds, setWishlistIds] = useState<number[]>([]);
  const [wishlistCount, setWishlistCount] = useState(0);

  useEffect(() => {
    loadWishlist();
    
    // Listen for wishlist updates
    const handleWishlistUpdate = () => {
      loadWishlist();
    };
    
    window.addEventListener('wishlist-updated', handleWishlistUpdate);
    return () => window.removeEventListener('wishlist-updated', handleWishlistUpdate);
  }, []);

  const loadWishlist = () => {
    const saved = localStorage.getItem('vehicle-wishlist');
    if (saved) {
      try {
        const parsed: WishlistVehicle[] = JSON.parse(saved);
        setWishlistIds(parsed.map(v => v.id));
        setWishlistCount(parsed.length);
      } catch (error) {
        console.error('Error loading wishlist:', error);
      }
    } else {
      setWishlistIds([]);
      setWishlistCount(0);
    }
  };

  const isInWishlist = (vehicleId: number): boolean => {
    return wishlistIds.includes(vehicleId);
  };

  const addToWishlist = (vehicle: Omit<WishlistVehicle, 'addedDate'>) => {
    const saved = localStorage.getItem('vehicle-wishlist');
    const existing: WishlistVehicle[] = saved ? JSON.parse(saved) : [];
    
    // Check if already in wishlist
    if (existing.some(v => v.id === vehicle.id)) {
      return;
    }
    
    const newVehicle: WishlistVehicle = {
      ...vehicle,
      addedDate: new Date().toISOString()
    };
    
    const updated = [...existing, newVehicle];
    localStorage.setItem('vehicle-wishlist', JSON.stringify(updated));
    setWishlistIds(updated.map(v => v.id));
    setWishlistCount(updated.length);
    
    // Dispatch event
    window.dispatchEvent(new CustomEvent('wishlist-updated'));
  };

  const removeFromWishlist = (vehicleId: number) => {
    const saved = localStorage.getItem('vehicle-wishlist');
    if (!saved) return;
    
    const existing: WishlistVehicle[] = JSON.parse(saved);
    const updated = existing.filter(v => v.id !== vehicleId);
    
    localStorage.setItem('vehicle-wishlist', JSON.stringify(updated));
    setWishlistIds(updated.map(v => v.id));
    setWishlistCount(updated.length);
    
    // Dispatch event
    window.dispatchEvent(new CustomEvent('wishlist-updated'));
  };

  const toggleWishlist = (vehicle: Omit<WishlistVehicle, 'addedDate'>) => {
    if (isInWishlist(vehicle.id)) {
      removeFromWishlist(vehicle.id);
    } else {
      addToWishlist(vehicle);
    }
  };

  return {
    wishlistIds,
    wishlistCount,
    isInWishlist,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist
  };
}
